import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PasswordInput } from "@/components/app/PasswordInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe2, ArrowRight, ShoppingCart, Factory } from "lucide-react";
import { toast } from "sonner";
import { friendlyError } from "@/lib/api";

export default function Signup() {
  const { signup } = useAuth();
  const [role, setRole] = useState("buyer");
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company_name: "", country: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      const u = await signup({ ...form, role });
      toast.success("Account created");
      const next = params.get("next");
      nav(next || `/${u.role}`);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[hsl(var(--navy))] text-slate-100">
      <div className="flex items-center justify-center p-6 md:p-12 bg-slate-50 text-slate-900 order-2 md:order-1">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="signup-form">
          <div className="md:hidden mb-4 flex items-center gap-2">
            <span className="h-8 w-8 rounded-sm bg-[hsl(var(--gold))] flex items-center justify-center text-[hsl(var(--navy))]"><Globe2 size={18}/></span>
            <span className="font-display text-xl text-[hsl(var(--navy))]">GlobalBuy <span className="text-[hsl(var(--gold))]">AI</span></span>
          </div>
          <div>
            <h1 className="font-display text-3xl">Create your account</h1>
            <p className="text-sm text-slate-500 mt-1">Join as a buyer or a supplier.</p>
          </div>

          <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Account role">
            {[
              { v: "buyer", label: "Buyer", Icon: ShoppingCart, desc: "I need to source products or services." },
              { v: "supplier", label: "Supplier", Icon: Factory, desc: "I supply products or services." },
            ].map(({ v, label, Icon, desc }) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={role === v}
                onClick={() => setRole(v)}
                data-testid={`signup-role-${v}`}
                className={`text-left rounded-md border p-4 transition-colors ${role === v ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/5" : "border-slate-200 hover:border-slate-300"}`}
              >
                <Icon size={18} className={role === v ? "text-[hsl(var(--gold))]" : "text-slate-500"}/>
                <div className="font-display text-base mt-2">{label}</div>
                <div className="text-xs text-slate-500 mt-1 leading-snug">{desc}</div>
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="full_name">Full name</Label>
            <Input id="full_name" required value={form.full_name} onChange={update("full_name")} data-testid="signup-fullname-input"/>
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_name">Company name {role === "supplier" && <span className="text-[hsl(var(--gold))]">·</span>}</Label>
            <Input id="company_name" value={form.company_name} onChange={update("company_name")} data-testid="signup-company-input" placeholder="Optional for buyers"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="signup-email-input" autoComplete="email"/>
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input id="country" value={form.country} onChange={update("country")} data-testid="signup-country-input" placeholder="e.g. Germany"/>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password <span className="text-xs text-slate-400">· min 6 chars</span></Label>
            <PasswordInput id="password" required value={form.password} onChange={update("password")} autoComplete="new-password" testIdPrefix="signup-password"/>
          </div>
          <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="signup-submit-btn">
            {loading ? "Creating…" : <>Create account <ArrowRight size={16}/></>}
          </button>
          <div className="text-sm text-slate-500 text-center">
            Have an account? <Link to="/login" className="text-[hsl(var(--navy))] font-medium hover:underline" data-testid="signup-to-login-link">Sign in</Link>
          </div>
        </form>
      </div>

      <div className="hidden md:block relative order-1 md:order-2">
        <img src="https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxnbG9iYWwlMjB0cmFkZSUyMGxvZ2lzdGljcyUyMGFic3RyYWN0fGVufDB8fHx8MTc5MTI3MDk5OXww&ixlib=rb-4.1.0&q=85" className="absolute inset-0 w-full h-full object-cover opacity-50"/>
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--navy))]/80 via-[hsl(var(--navy))]/60 to-[hsl(var(--navy))]"/>
        <div className="relative z-10 h-full flex flex-col justify-between p-10">
          <Link to="/" className="flex items-center gap-2 text-slate-100">
            <span className="h-8 w-8 rounded-sm bg-[hsl(var(--gold))] flex items-center justify-center text-[hsl(var(--navy))]"><Globe2 size={18}/></span>
            <span className="font-display text-xl">GlobalBuy <span className="text-[hsl(var(--gold))]">AI</span></span>
          </Link>
          <div>
            <div className="eyebrow mb-3">For global sourcing teams</div>
            <div className="font-display text-3xl leading-tight max-w-md">From informal need to structured RFQ in minutes — not days.</div>
          </div>
          <div className="text-xs text-slate-400">Human-approved purchasing · Transparent AI reasoning · Privacy-first</div>
        </div>
      </div>
    </div>
  );
}
